import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Chip,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  Button,
  Card,
  CardContent,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Gavel as GavelIcon,
  Visibility as ViewIcon,
  FavoriteBorder as FavoriteIcon,
  Menu as MenuIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HelpOutline as UncertainIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import AuthContext from '../contexts/AuthContext';
import axios from 'axios';

// Interfacce
interface CaseReference {
  id: string;
  title: string;
  outcome: 'ACCOLTO' | 'RIGETTATO' | 'PARZIALE' | 'INCERTO';
  snippet: string;
  relevance: number;
  cities: string[];
}

interface AnalysisResult {
  probability: number;
  totalCases: number;
  favorableCases: number;
  unfavorableCases: number;
  partialCases: number;
  positiveFactors: string[];
  riskFactors: string[];
  strategicSuggestions: string[];
  referenceCases: CaseReference[];
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  analysisResult?: AnalysisResult;
  timestamp?: Date;
}

interface AnalysisSession {
  id: string;
  title: string;
  probability: number | null;
  updatedAt: string;
  messageCount: number;
}

const BookyTribunalePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { token } = useContext(AuthContext);
  
  // States
  const [messages, setMessages] = useState<Message[]>([]);
  const [caseDescription, setCaseDescription] = useState('');
  const [selectedTribunal, setSelectedTribunal] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessions, setSessions] = useState<AnalysisSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [tribunali, setTribunali] = useState<string[]>([]);

  // Limite utilizzo
  const [usageLimit, setUsageLimit] = useState<{
    isAdmin: boolean;
    unlimited: boolean;
    dailyLimit: number;
    used: number;
    remaining: number;
    credits?: number;
    creditCost?: number;
  } | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Carica sessioni e limite all'avvio
  useEffect(() => {
    loadSessions();
    loadUsageLimit();
    loadTribunali();
  }, [token]);

  // Scroll automatico
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadUsageLimit = async () => {
    try {
      const response = await axios.get<{
        isAdmin: boolean;
        unlimited: boolean;
        dailyLimit: number;
        used: number;
        remaining: number;
        credits?: number;
        creditCost?: number;
      }>('/api/booky-tribunale/limit');
      setUsageLimit(response.data);
      // Aggiorna il saldo crediti mostrato nella Navbar
      if (typeof response.data.credits === 'number') {
        window.dispatchEvent(new CustomEvent('credits-updated', { detail: response.data.credits }));
      }
    } catch (error) {
      console.error('Errore caricamento limite:', error);
    }
  };

  const loadTribunali = async () => {
    try {
      const response = await axios.get<{ tribunali: string[] }>('/api/documents/tribunali');
      setTribunali(response.data.tribunali || []);
    } catch (error) {
      console.error('Errore caricamento tribunali:', error);
    }
  };

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await axios.get<{ sessions: AnalysisSession[] }>('/api/booky-tribunale/sessions');
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Errore caricamento sessioni:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (sessionId: string) => {
    try {
      const response = await axios.get<{ messages: Message[] }>(`/api/booky-tribunale/sessions/${sessionId}`);
      setMessages(response.data.messages || []);
      setCurrentSessionId(sessionId);
      if (isMobile) setSidebarOpen(false);
    } catch (error) {
      console.error('Errore caricamento sessione:', error);
    }
  };

  const deleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await axios.delete(`/api/booky-tribunale/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSessionId === sessionId) {
        startNewAnalysis();
      }
    } catch (error) {
      console.error('Errore eliminazione sessione:', error);
    }
  };

  const startNewAnalysis = () => {
    setMessages([]);
    setCurrentSessionId(null);
    setCaseDescription('');
    setSelectedTribunal(null);
    inputRef.current?.focus();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!caseDescription.trim() || isLoading) return;

    // Blocca solo se ha esaurito le gratuite E non ha crediti acquistati sufficienti
    if (usageLimit && !usageLimit.unlimited && usageLimit.remaining <= 0 && (usageLimit.credits ?? 0) < (usageLimit.creditCost ?? 1)) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `⚠️ Hai raggiunto il limite mensile di ${usageLimit.dailyLimit} analisi predittive. Il limite si resetta al termine del mese solare a mezzanotte.`,
        timestamp: new Date()
      }]);
      return;
    }

    const userMessage: Message = {
      role: 'user',
      content: caseDescription.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCaseDescription('');
    setIsLoading(true);

    try {
      const response = await axios.post('/api/booky-tribunale', {
        caseDescription: userMessage.content,
        tribunal: selectedTribunal,
        sessionId: currentSessionId
      });

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.data.message,
        analysisResult: response.data.analysisResult,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (!currentSessionId && response.data.sessionId) {
        setCurrentSessionId(response.data.sessionId);
        loadSessions();
      }

      loadUsageLimit();
    } catch (error: any) {
      console.error('Errore analisi:', error);
      
      if (error.response?.status === 429) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `⚠️ ${error.response.data.message || 'Limite giornaliero raggiunto.'}`,
          timestamp: new Date()
        }]);
        loadUsageLimit();
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Mi dispiace, si è verificato un errore durante l\'analisi. Riprova tra qualche istante.',
          timestamp: new Date()
        }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDocument = (docId: string) => {
    navigate(`/viewer/${docId}`);
  };

  const handleAddToFavorites = async (docId: string) => {
    try {
      await axios.post('/api/documents/favorites', { documentId: docId });
    } catch (error) {
      console.error('Errore aggiunta preferiti:', error);
    }
  };

  // Componente per il grafico della probabilità
  const ProbabilityMeter: React.FC<{ probability: number }> = ({ probability }) => {
    const getColor = () => {
      if (probability >= 70) return '#4CAF50';
      if (probability >= 50) return '#FFC107';
      if (probability >= 30) return '#FF9800';
      return '#F44336';
    };

    const getLabel = () => {
      if (probability >= 70) return 'ALTA';
      if (probability >= 50) return 'MEDIA';
      if (probability >= 30) return 'BASSA';
      return 'MOLTO BASSA';
    };

    return (
      <Box sx={{ 
        textAlign: 'center', 
        p: 3, 
        bgcolor: 'rgba(0,0,0,0.02)', 
        borderRadius: '8px',
        border: `2px solid ${getColor()}20`
      }}>
        <Typography variant="overline" sx={{ color: 'var(--text-secondary)' }}>
          PROBABILITÀ DI ESITO FAVOREVOLE
        </Typography>
        
        <Box sx={{ position: 'relative', my: 2 }}>
          <LinearProgress
            variant="determinate"
            value={probability}
            sx={{
              height: 24,
              borderRadius: '12px',
              bgcolor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: getColor(),
                borderRadius: '12px',
              }
            }}
          />
          <Typography
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: probability > 50 ? 'white' : 'var(--text-primary)'
            }}
          >
            {probability}%
          </Typography>
        </Box>
        
        <Chip
          label={`${getLabel()} PROBABILITÀ`}
          sx={{
            bgcolor: `${getColor()}20`,
            color: getColor(),
            fontWeight: 700,
            fontSize: '0.8rem'
          }}
        />
      </Box>
    );
  };

  // Componente per i fattori
  const FactorsList: React.FC<{ 
    title: string; 
    items: string[]; 
    icon: React.ReactNode;
    color: string;
  }> = ({ title, items, icon, color }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color }}>{title}</Typography>
      </Box>
      {items.map((item, i) => (
        <Typography 
          key={i} 
          variant="body2" 
          sx={{ 
            pl: 3, 
            py: 0.5,
            color: 'var(--text-secondary)',
            borderLeft: `2px solid ${color}40`
          }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );

  // Outcome icon
  const OutcomeIcon: React.FC<{ outcome: string }> = ({ outcome }) => {
    switch (outcome) {
      case 'ACCOLTO':
        return <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />;
      case 'RIGETTATO':
        return <CancelIcon sx={{ color: '#F44336', fontSize: 20 }} />;
      case 'PARZIALE':
        return <TrendingUpIcon sx={{ color: '#FF9800', fontSize: 20 }} />;
      default:
        return <UncertainIcon sx={{ color: '#9E9E9E', fontSize: 20 }} />;
    }
  };

  // Sidebar Component
  const SidebarContent = () => (
    <Box sx={{ 
      width: isMobile ? 280 : 300, 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'var(--bg-secondary)',
      borderRight: '1px solid var(--border-light)'
    }}>
      <Box sx={{ p: 2, borderBottom: '1px solid var(--border-light)' }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={startNewAnalysis}
          sx={{
            bgcolor: '#7B1FA2',
            '&:hover': { bgcolor: '#6A1B9A' },
            borderRadius: '4px',
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Nuova Analisi
        </Button>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
        <Typography variant="caption" sx={{ px: 1, color: 'var(--text-tertiary)', fontWeight: 600 }}>
          ANALISI SALVATE
        </Typography>
        
        {loadingSessions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : sessions.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, color: 'var(--text-tertiary)', textAlign: 'center' }}>
            Nessuna analisi salvata
          </Typography>
        ) : (
          <List dense>
            {sessions.map((session) => (
              <ListItem
                key={session.id}
                disablePadding
                secondaryAction={
                  <IconButton 
                    edge="end" 
                    size="small"
                    onClick={(e) => deleteSession(session.id, e)}
                    sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemButton
                  selected={currentSessionId === session.id}
                  onClick={() => loadSession(session.id)}
                  sx={{ 
                    borderRadius: '4px',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(123, 31, 162, 0.1)',
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <GavelIcon fontSize="small" sx={{ color: '#7B1FA2' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={session.title}
                    secondary={session.probability !== null ? `${session.probability}% prob.` : ''}
                    primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Sidebar Desktop */}
      {!isMobile && sidebarOpen && <SidebarContent />}

      {/* Sidebar Mobile */}
      <Drawer
        anchor="left"
        open={isMobile && sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <SidebarContent />
      </Drawer>

      {/* Area Principale */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'var(--bg-primary)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            borderBottom: '1px solid var(--border-light)',
            borderRadius: 0
          }}
        >
          {isMobile && (
            <IconButton onClick={() => setSidebarOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <GavelIcon sx={{ color: '#7B1FA2', fontSize: 28 }} />
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: '"Libre Baskerville", serif',
                fontWeight: 700,
                color: '#7B1FA2'
              }}
            >
              BOOKY TRIBUNALE
            </Typography>
            <Chip 
              label="ANALISI PREDITTIVA" 
              size="small" 
              sx={{ 
                bgcolor: 'rgba(123, 31, 162, 0.1)', 
                color: '#7B1FA2',
                fontWeight: 700,
                fontSize: '0.65rem'
              }} 
            />
          </Box>
          
          {/* Indicatore limite */}
          {usageLimit && (
            <Chip
              label={usageLimit.unlimited 
                ? '∞ Illimitato' 
                : `${usageLimit.remaining}/${usageLimit.dailyLimit} analisi mensili gratuite rimanenti${usageLimit.credits ? ` · ${usageLimit.credits} crediti` : ''}`
              }
              size="small"
              sx={{
                bgcolor: usageLimit.unlimited 
                  ? 'rgba(76, 175, 80, 0.1)' 
                  : (usageLimit.remaining > 0 || (usageLimit.credits ?? 0) >= (usageLimit.creditCost ?? 1)) 
                    ? 'rgba(123, 31, 162, 0.1)' 
                    : 'rgba(244, 67, 54, 0.1)',
                color: usageLimit.unlimited 
                  ? '#388E3C' 
                  : (usageLimit.remaining > 0 || (usageLimit.credits ?? 0) >= (usageLimit.creditCost ?? 1)) 
                    ? '#7B1FA2' 
                    : '#D32F2F',
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
          )}
        </Paper>

        {/* Area Messaggi */}
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          p: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}>
          {messages.length === 0 ? (
            // Stato iniziale
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 3,
              maxWidth: 700,
              mx: 'auto',
              textAlign: 'center',
              p: 3
            }}>
              <Box
                component="img"
                src="/booky.png"
                alt="Booky"
                sx={{
                  width: { xs: 100, md: 130 },
                  height: { xs: 100, md: 130 },
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 12px rgba(123,31,162,0.3))',
                }}
              />
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: '"Libre Baskerville", serif',
                  fontWeight: 700,
                  color: 'var(--text-primary)'
                }}
              >
                Analisi Predittiva del Caso
              </Typography>
              <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                Descrivi il tuo caso in dettaglio. Analizzerò la giurisprudenza esistente 
                e calcolerò la probabilità di successo basandomi su sentenze simili.
              </Typography>
              
              {/* Esempi */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 2 }}>
                {[
                  'Danno biologico da sinistro stradale',
                  'Licenziamento per giusta causa',
                  'Nullità clausole bancarie',
                  'Responsabilità medica'
                ].map((example) => (
                  <Chip
                    key={example}
                    label={example}
                    variant="outlined"
                    onClick={() => setCaseDescription(example + '. ')}
                    sx={{ 
                      cursor: 'pointer',
                      borderColor: 'rgba(123, 31, 162, 0.3)',
                      '&:hover': { 
                        bgcolor: 'rgba(123, 31, 162, 0.05)',
                        borderColor: '#7B1FA2'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            // Messaggi
            <>
              {messages.map((msg, idx) => (
                <Box 
                  key={idx}
                  sx={{ 
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    width: '100%'
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      maxWidth: { xs: '95%', md: '85%' },
                      bgcolor: msg.role === 'user' ? '#7B1FA2' : 'white',
                      color: msg.role === 'user' ? 'white' : 'var(--text-primary)',
                      border: msg.role === 'assistant' ? '1px solid var(--border-light)' : 'none',
                      borderRadius: '12px',
                    }}
                  >
                    {/* Contenuto messaggio utente */}
                    {msg.role === 'user' && (
                      <Typography sx={{ lineHeight: 1.7 }}>
                        {msg.content}
                      </Typography>
                    )}

                    {/* Risultato analisi */}
                    {msg.role === 'assistant' && msg.analysisResult && (
                      <Box>
                        {/* Probabilità */}
                        <ProbabilityMeter probability={msg.analysisResult.probability} />

                        {/* Statistiche */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          gap: 3, 
                          my: 3,
                          flexWrap: 'wrap'
                        }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                              {msg.analysisResult.favorableCases}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">Favorevoli</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#F44336' }}>
                              {msg.analysisResult.unfavorableCases}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">Sfavorevoli</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF9800' }}>
                              {msg.analysisResult.partialCases}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">Parziali</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--primary-color)' }}>
                              {msg.analysisResult.totalCases}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">Totali</Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Fattori Positivi */}
                        {msg.analysisResult.positiveFactors.length > 0 && (
                          <FactorsList
                            title="FATTORI POSITIVI"
                            items={msg.analysisResult.positiveFactors}
                            icon={<CheckIcon />}
                            color="#4CAF50"
                          />
                        )}

                        {/* Fattori di Rischio */}
                        {msg.analysisResult.riskFactors.length > 0 && (
                          <FactorsList
                            title="FATTORI DI RISCHIO"
                            items={msg.analysisResult.riskFactors}
                            icon={<WarningIcon />}
                            color="#FF9800"
                          />
                        )}

                        {/* Suggerimenti */}
                        {msg.analysisResult.strategicSuggestions.length > 0 && (
                          <FactorsList
                            title="SUGGERIMENTI STRATEGICI"
                            items={msg.analysisResult.strategicSuggestions}
                            icon={<LightbulbIcon />}
                            color="#7B1FA2"
                          />
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* Sentenze di Riferimento */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'var(--text-primary)' }}>
                          📄 SENTENZE DI RIFERIMENTO
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {msg.analysisResult.referenceCases.map((refCase) => (
                            <Card 
                              key={refCase.id}
                              variant="outlined"
                              sx={{ 
                                borderRadius: '8px',
                                borderLeft: `4px solid ${
                                  refCase.outcome === 'ACCOLTO' ? '#4CAF50' :
                                  refCase.outcome === 'RIGETTATO' ? '#F44336' :
                                  refCase.outcome === 'PARZIALE' ? '#FF9800' : '#9E9E9E'
                                }`
                              }}
                            >
                              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <OutcomeIcon outcome={refCase.outcome} />
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                    {refCase.title}
                                  </Typography>
                                  <Chip 
                                    label={refCase.outcome} 
                                    size="small"
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      bgcolor: refCase.outcome === 'ACCOLTO' ? '#E8F5E9' :
                                               refCase.outcome === 'RIGETTATO' ? '#FFEBEE' :
                                               refCase.outcome === 'PARZIALE' ? '#FFF3E0' : '#F5F5F5',
                                      color: refCase.outcome === 'ACCOLTO' ? '#2E7D32' :
                                             refCase.outcome === 'RIGETTATO' ? '#C62828' :
                                             refCase.outcome === 'PARZIALE' ? '#E65100' : '#616161'
                                    }}
                                  />
                                </Box>
                                
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: 'var(--text-secondary)',
                                    fontSize: '0.85rem',
                                    mb: 1.5
                                  }}
                                  dangerouslySetInnerHTML={{ __html: refCase.snippet }}
                                />
                                
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    startIcon={<ViewIcon />}
                                    onClick={() => handleViewDocument(refCase.id)}
                                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                                  >
                                    Visualizza
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={<FavoriteIcon />}
                                    onClick={() => handleAddToFavorites(refCase.id)}
                                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                                  >
                                    Preferiti
                                  </Button>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>

                        {/* Disclaimer */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            mt: 3, 
                            p: 1.5, 
                            bgcolor: 'rgba(0,0,0,0.03)', 
                            borderRadius: '4px',
                            color: 'var(--text-tertiary)'
                          }}
                        >
                          ⚠️ Questa analisi è indicativa e basata su dati storici. 
                          Non sostituisce il parere professionale di un avvocato.
                        </Typography>
                      </Box>
                    )}

                    {/* Messaggio senza analisi (errori, limite, ecc) */}
                    {msg.role === 'assistant' && !msg.analysisResult && (
                      <Box sx={{
                        lineHeight: 1.7,
                        '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                        '& strong': { fontWeight: 700, color: '#7B1FA2' },
                        '& em': { fontStyle: 'italic', color: 'var(--text-secondary)' },
                        '& ul, & ol': { pl: 2.5, mb: 1 },
                        '& li': { mb: 0.5 },
                        '& h1, & h2, & h3': { fontFamily: 'Syne, sans-serif', fontWeight: 700, color: '#7B1FA2', mt: 2, mb: 1 },
                        '& hr': { borderColor: 'var(--border-light)', my: 1.5 },
                        '& blockquote': { borderLeft: '3px solid #CE93D8', pl: 1.5, ml: 0, color: 'var(--text-secondary)', fontStyle: 'italic' },
                      }}>
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </Box>
                    )}
                  </Paper>
                </Box>
              ))}
              
              {isLoading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  <CircularProgress size={32} sx={{ color: '#7B1FA2' }} />
                  <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
                    Analizzo le sentenze e calcolo la probabilità...
                  </Typography>
                </Box>
              )}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>

        {/* Input Area */}
        <Paper 
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{ 
            p: 2, 
            borderTop: '1px solid var(--border-light)',
            borderRadius: 0
          }}
        >
          <Box sx={{ 
            maxWidth: 900,
            mx: 'auto'
          }}>
            {/* Filtro Tribunale */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Autocomplete
                size="small"
                options={tribunali}
                value={selectedTribunal}
                onChange={(_, value) => setSelectedTribunal(value)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Tribunale (opzionale)"
                    sx={{ minWidth: 200 }}
                  />
                )}
                sx={{ flexGrow: 1, maxWidth: 300 }}
              />
            </Box>

            {/* Textarea + Button */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                inputRef={inputRef}
                fullWidth
                placeholder="Descrivi il tuo caso in dettaglio... (es: 'Il mio cliente ha subito un danno biologico del 18% in un sinistro stradale...')"
                value={caseDescription}
                onChange={(e) => setCaseDescription(e.target.value)}
                disabled={isLoading}
                multiline
                minRows={2}
                maxRows={6}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: 'var(--bg-secondary)'
                  }
                }}
              />
              <IconButton 
                type="submit"
                disabled={!caseDescription.trim() || isLoading}
                sx={{ 
                  bgcolor: '#7B1FA2',
                  color: 'white',
                  '&:hover': { bgcolor: '#6A1B9A' },
                  '&.Mui-disabled': { bgcolor: 'var(--border-light)' },
                  width: 56,
                  height: 56,
                  alignSelf: 'flex-end'
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default BookyTribunalePage;

